import { englishError } from "../utils/databaseErrors";
import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { availableQuantity, businessDate, PRODUCT_TYPES, reservationStatus } from "../utils/stock";
import { validMarbleQuantity, sumQuantities } from "../utils/marbleQuantity";
import "./ReservationTable.css";

const STATUS = { active: "Active", expired: "Expired", cancelled: "Cancelled", fulfilled: "Sold" };
function emptyForm() {
  const expiry = new Date(`${businessDate()}T12:00:00Z`);
  expiry.setUTCDate(expiry.getUTCDate() + 30);
  return { distribution_id: "", customer_id: "", slabs_reserved: "", notes: "", expire_date: businessDate(expiry) };
}

export default function ReservationTable({ user }) {
  const [reservations, setReservations] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.showroom_id]);

  async function fetchAll() {
    setLoading(true); setError("");
    try {
      if (!user.showroom_id) throw new Error("Assign a showroom to this user to manage reservations.");
      const results = await Promise.all([
        supabase.from("reservation").select("*").order("id", { ascending: false }),
        supabase.from("block_distribution").select("*").eq("showroom_id", user.showroom_id),
        supabase.from("customer").select("id, name, phone, roles"),
        supabase.from("block").select("*"),
      ]);
      const failed = results.find((result) => result.error);
      if (failed) throw failed.error;
      const stock = results[1].data || [];
      const ids = new Set(stock.map((row) => String(row.id)));
      const bookings = (results[0].data || []).filter((row) => ids.has(String(row.distribution_id)));
      setReservations(bookings); setDistributions(stock);
      setCustomers(results[2].data || []); setBlocks(results[3].data || []);
      return { bookings, stock };
    } catch (err) {
      setError("Unable to load reservations: " + englishError(err.message)); return null;
    } finally { setLoading(false); }
  }

  const customerName = (id) => customers.find((row) => String(row.id) === String(id))?.name || "-";
  function productLabel(distribution) {
    const product = blocks.find((row) => String(row.id) === String(distribution?.block_id));
    const face = { epoxy: "Epoxy", resin: "Resin", none: "None" };
    const variant = distribution?.variant_suffix || `Front ${face[distribution?.face_treatment || "none"]} / Back ${face[distribution?.back_treatment || "none"]}${distribution?.is_polished ? " · Polished" : ""}`;
    return `${product?.code || "-"} · ${PRODUCT_TYPES[product?.product_type || "slab"]} · ${variant}`;
  }
  function reset() { setForm(emptyForm()); setEditId(null); }
  function change(event) { setForm((current) => ({ ...current, [event.target.name]: event.target.value })); }

  async function save(event) {
    event.preventDefault();
    if (busy) return;
    setError(""); setMessage("");
    const quantity = Number(form.slabs_reserved);
    if (!validMarbleQuantity(quantity) || !form.distribution_id || !form.customer_id) {
      setError("Select a product and customer and enter a positive quantity."); return;
    }
    if (form.expire_date < businessDate()) { setError("Select an expiry date of today or later."); return; }
    setBusy(true);
    try {
      const latest = await fetchAll();
      if (!latest) return;
      const distribution = latest.stock.find((row) => String(row.id) === String(form.distribution_id));
      const existing = latest.bookings.find((row) => String(row.id) === String(editId));
      if (editId != null && (!existing || reservationStatus(existing) !== "active")) throw new Error("This reservation is no longer active. Refresh the list.");
      const available = availableQuantity(distribution, latest.bookings, editId);
      if (!distribution || quantity > available) throw new Error(`Available to reserve ${available} pieces only.`);
      const payload = {
        distribution_id: Number(form.distribution_id), customer_id: Number(form.customer_id),
        slabs_reserved: quantity, notes: form.notes.trim(), expire_date: form.expire_date,
        reservation_date: existing?.reservation_date || businessDate(), status: "active",
      };
      const query = editId == null
        ? supabase.from("reservation").insert([payload]).select("id")
        : supabase.from("reservation").update(payload).eq("id", editId).eq("status", "active").select("id");
      const result = await query;
      if (result.error) throw result.error;
      if (!result.data?.length) throw new Error("Reservation was not saved. Refresh and try again.");
      reset(); setMessage("Reservation saved. The quantity is reserved until expiry, cancellation, or sale.");
      await fetchAll();
    } catch (err) { setError("Unable to save reservation: " + englishError(err.message)); }
    finally { setBusy(false); }
  }

  async function cancel(row) {
    if (busy || !window.confirm("Cancel this reservation and release its stock?")) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await supabase.from("reservation").update({ status: "cancelled" })
        .eq("id", row.id).eq("status", "active").select("id");
      if (result.error) throw result.error;
      if (!result.data?.length) throw new Error("The reservation has already changed. Refresh the list.");
      if (String(editId) === String(row.id)) reset();
      setMessage("Reservation cancelled and stock released."); await fetchAll();
    } catch (err) { setError("Unable to cancel reservation: " + englishError(err.message)); }
    finally { setBusy(false); }
  }

  const selected = distributions.find((row) => String(row.id) === String(form.distribution_id));
  const visible = reservations.filter((row) => {
    const distribution = distributions.find((dist) => String(dist.id) === String(row.distribution_id));
    return (filter === "all" || reservationStatus(row) === filter) &&
      `${customerName(row.customer_id)} ${productLabel(distribution)} ${row.notes || ""}`.toLowerCase().includes(search.toLowerCase());
  });
  const active = reservations.filter((row) => reservationStatus(row) === "active");

  return (
    <section className="reservations" dir="ltr">
      <div className="reservation-summary">
        <div>Active reservations <strong>{active.length}</strong></div>
        <div>Reserved quantity <strong>{sumQuantities(active.map(row => row.slabs_reserved))} pieces</strong></div>
      </div>
      {error && <p className="reservation-error" role="alert">{error}</p>}
      {message && <p className="reservation-success" role="status">{message}</p>}
      <h3>{editId == null ? "New reservation" : "Edit reservation"}</h3>
      <form onSubmit={save}>
        <fieldset disabled={loading || busy} className="reservation-form">
          <label>Product / Finish<select name="distribution_id" value={form.distribution_id} onChange={change} required>
            <option value="">Select a product</option>
            {distributions.map((distribution) => {
              const available = availableQuantity(distribution, reservations, editId);
              if (!available && String(distribution.id) !== String(form.distribution_id)) return null;
              return <option key={distribution.id} value={distribution.id}>{productLabel(distribution)} — {available} Available</option>;
            })}
          </select></label>
          <label>Customer<select name="customer_id" value={form.customer_id} onChange={change} required>
            <option value="">Select a customer</option>
            {customers.filter((row) => !row.roles?.length || row.roles.includes("عميل")).map((row) => <option key={row.id} value={row.id}>{row.name} {row.phone ? `· ${row.phone}` : ""}</option>)}
          </select></label>
          <label>Quantity<input name="slabs_reserved" type="number" min="0.001" step="0.001" max={availableQuantity(selected, reservations, editId)} value={form.slabs_reserved} onChange={change} required /></label>
          <label>Expiry date<input name="expire_date" type="date" min={businessDate()} value={form.expire_date} onChange={change} required /></label>
          <label>Notes<input name="notes" value={form.notes} onChange={change} /></label>
          <div className="reservation-actions"><button type="submit">{busy ? "Saving…" : editId == null ? "Save reservation" : "Update reservation"}</button>
            {editId != null && <button type="button" onClick={reset}>Cancel editing</button>}</div>
        </fieldset>
      </form>
      <div className="reservation-filters">
        <label>Search<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Customer, Product code, Notes" /></label>
        <label>Status<select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">All</option>{Object.entries(STATUS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        <button type="button" onClick={fetchAll} disabled={busy || loading}>Refresh</button>
      </div>
      <div className="reservation-table"><table><thead><tr>
        <th>Reservation number</th><th>Product</th><th>Customer</th><th>Quantity</th><th>Reservation date</th><th>Expiry</th><th>Status</th><th>Notes</th><th>Actions</th>
      </tr></thead><tbody>
        {visible.map((row) => {
          const status = reservationStatus(row);
          const distribution = distributions.find((dist) => String(dist.id) === String(row.distribution_id));
          return <tr key={row.id}><td>{row.id}</td><td>{productLabel(distribution)}</td><td>{customerName(row.customer_id)}</td><td>{row.slabs_reserved}</td><td>{row.reservation_date?.slice(0, 10)}</td><td>{row.expire_date?.slice(0, 10) || "-"}</td><td>{STATUS[status]}{row.sale_id ? ` #${row.sale_id}` : ""}</td><td>{row.notes || "-"}</td><td>
            {status === "active" && <div className="reservation-actions"><button disabled={busy} onClick={() => {
              setEditId(row.id); setForm({ distribution_id: row.distribution_id, customer_id: row.customer_id, slabs_reserved: row.slabs_reserved, notes: row.notes || "", expire_date: row.expire_date?.slice(0, 10) || emptyForm().expire_date }); setMessage("");
            }}>Edit</button><button disabled={busy} onClick={() => cancel(row)}>Cancel reservation</button></div>}
          </td></tr>;
        })}
        {!visible.length && <tr><td colSpan="9">{loading ? "Loading…" : "No matching reservations."}</td></tr>}
      </tbody></table></div>
    </section>
  );
}
